<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modalTitle"
    @on-confirm="saveContentTypeFieldsRights"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    maxWidth="60%"
  >
    <v-row v-if="fieldsRightsItems.length == 0">
      <v-col cols="1" align-self="center">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              tile
              large
              color="primary"
              icon
              @click="addContentTypeFieldsRights()"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("Add") }}</span>
        </v-tooltip>
      </v-col>
    </v-row>
    <div v-for="(item, index) in fieldsRightsItems" :key="item.uuid">
      <v-row>
        <v-col cols="1" align-self="center">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                tile
                large
                color="error"
                icon
                @click="removeContentTypeFieldsRights(item.uuid)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Delete") }}</span>
          </v-tooltip>
        </v-col>
        <v-col align-self="center">
          <v-text-field
            readonly
            v-model="item.displayConditionStr"
            :label="$t('Condition')"
            :append-icon="item.conditionStr == '' ? 'mdi-plus' : 'mdi-pencil'"
            @click:append="editCondition(item)"
          ></v-text-field>
        </v-col>
        <v-col align-self="center">
          <v-select
            :label="$t('Role')"
            v-model="item.roleTypeID"
            :items="rolesList"
            item-text="text"
            item-value="id"
            :error-messages="roleErrors(index)"
          ></v-select>
        </v-col>
        <v-col align-self="center">
          <v-select
            :label="$t('Field')"
            v-model="item.defFieldID"
            :items="contentType.fields"
            item-text="name"
            item-value="id"
            :error-messages="fieldErrors(index)"
          ></v-select>
        </v-col>
        <v-col cols="2" align-self="center">
          <v-tooltip top :disabled="!fieldRequired(item)">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" style="width: min-content">
                <v-switch
                  v-on="on"
                  v-model="item.visible"
                  :disabled="fieldRequired(item)"
                  :label="$t('Visible')"
                ></v-switch>
              </div>
            </template>
            <span>{{ $t("isRequired") }}</span>
          </v-tooltip>
        </v-col>
        <v-col>
          <v-switch v-model="item.readonly" :label="$t('Readonly')"></v-switch>
        </v-col>
        <v-col cols="1" align-self="center">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                tile
                large
                color="primary"
                icon
                @click="addContentTypeFieldsRights()"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Add") }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </div>
    <template v-slot:actions>
      <div style="position: absolute">
        <v-menu bottom right>
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  text
                  small
                  v-bind="attrs"
                  v-on="{ ...tooltip, ...menu }"
                  color="blue-grey"
                >
                  <v-icon large>mdi-history</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("Versions") }}</span>
            </v-tooltip>
          </template>
          <v-list>
            <v-list-item
              v-for="(item, i) in versionList"
              :key="i"
              @click="onVersionChange(item)"
            >
              <v-list-item-title>{{ "v" + item.text }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </template>
    <SearchCriteriaBuilderModal
      :ref="conditionModalKey"
      :contentTypeIDs="[contentType.id]"
      @on-condition-saved="onConditionChanged"
      :allowEmptyCondition="true"
    />
  </base-modal>
</template>
<script>
import SearchCriteriaBuilderModal from "@/components/search/SearchCriteriaBuilderModal.vue";
import ContentType from "@/ecm/models/contentType/ContentType";
import ContentTypeField from "@/ecm/models/contentType/ContentTypeField";
import ContentTypeFieldRight from "@/ecm/models/contentType/ContentTypeFieldRight";
import ContentTypeService from "@/services/ContentTypeService";
import RoleService from "@/services/RoleService";
import { required } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";

export default {
  name: "FieldsRightsModal",
  components: {
    SearchCriteriaBuilderModal,
  },
  mounted() {
    this.getRoles();
    this.$v.$reset();
  },
  data() {
    return {
      modal: {
        title: this.$t("FieldsAccessRights"),
        key: "contentTypeFieldsRightsModal",
      },
      contentType: new ContentType(),
      open: false,
      conditionModalOpen: false,
      fieldsRightsItems: [],
      currentItem: new ContentTypeFieldRight(),
      versionList: [],
      rolesList: [],
      accessFieldsRightsList: [],
      digitalSignaturesList: [],
      conditionModalKey: "conditionModalKey",
    };
  },
  mixins: [validationMixin],
  validations: {
    fieldsRightsItems: {
      $each: {
        roleTypeID: { required },
        defFieldID: { required },
      },
    },
  },
  computed: {
    modalTitle() {
      return `${this.modal.title} - ${this.contentType.name} ${this.contentType.version}`;
    },
    conditionModalInstance() {
      return this.$refs[this.conditionModalKey];
    },
  },
  methods: {
    roleErrors(index) {
      const errors = [];
      if (!this.$v.fieldsRightsItems.$each[index].roleTypeID.$dirty)
        return errors;
      !this.$v.fieldsRightsItems.$each[index].roleTypeID.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    fieldRequired(item) {
      let field = this.contentType.fields.find((f) => f.id === item.defFieldID);
      return field ? field.required : false;
    },
    fieldErrors(index) {
      const errors = [];
      if (!this.$v.fieldsRightsItems.$each[index].defFieldID.$dirty)
        return errors;
      !this.$v.fieldsRightsItems.$each[index].defFieldID.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    edit(dto) {
      this.contentType = new ContentType(dto);
      this.getContentTypeFieldsRights();
      this.getVersions();
      this.getFields();
      this.show();
    },
    editCondition(item) {
      this.currentItem = item;
      this.conditionModalInstance.edit(item.conditionStr);
    },
    onConditionChanged(condition) {
      this.currentItem.conditionStr = condition.conditionStr;
      this.currentItem.displayConditionStr = condition.displayConditionStr;
    },
    onVersionChange(version) {
      if (this.contentType.id != version.id) {
        this.contentType.id = version.id;
        this.contentType.version = "v" + version.text;
        this.getContentTypeFieldsRights();
        this.getFields();
      }
    },
    getContentTypeFieldsRights() {
      ContentTypeService.getFieldsRights(this.contentType.id).then((data) => {
        this.fieldsRightsItems = data.map(
          (dto) => new ContentTypeFieldRight(dto)
        );
      });
    },
    getVersions() {
      ContentTypeService.getVersions(this.contentType.id).then((data) => {
        this.versionList = data;
      });
    },
    getFields() {
      ContentTypeService.getFields([this.contentType.id]).then((data) => {
        this.contentType.fields = data.map(function (dto) {
          var field = new ContentTypeField(dto);
          field.id = field.id + "";
          return field;
        });
      });
    },
    getRoles() {
      RoleService.getList().then((data) => {
        this.rolesList = data;
      });
    },
    saveContentTypeFieldsRights() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }
      var fieldsRights = this.fieldsRightsItems.map((item) => item.toDto());
      ContentTypeService.saveFieldsRights(
        this.contentType.id,
        fieldsRights
      ).then((data) => {
        this.$emit("on-fields-rights-saved", this.fieldsRightsItems);
        this.hide();
      });
    },
    addContentTypeFieldsRights() {
      this.fieldsRightsItems.push(new ContentTypeFieldRight());
    },
    removeContentTypeFieldsRights(uuid) {
      this.fieldsRightsItems = this.fieldsRightsItems.filter(
        (item) => item.uuid !== uuid
      );
    },
    show() {
      this.open = true;
    },
    hide() {
      this.open = false;
    },
  },
};
</script>
